'use strict';

const parseJson = (value, fallback = null) => {
  if (value && typeof value === 'object') return value;
  try { return JSON.parse(value) ?? fallback; } catch (_) { return fallback; }
};
const roleKey = value => String(value || '').toLowerCase().replace(/[ _-]/g, '');
const isAppealDecisionMaker = role => ['nwacadministrator', 'systemadministrator'].includes(roleKey(role));
const appealError = (code, message, status = 409) => Object.assign(new Error(code), { code, publicMessage: message, status });
const positiveId = value => {
  if (!['number', 'string'].includes(typeof value) || (typeof value === 'string' && !/^\d+$/.test(value))) throw appealError('appeal_invalid_id', 'Choose a valid record.', 422);
  const id = Number(value);
  if (!Number.isSafeInteger(id) || id <= 0) throw appealError('appeal_invalid_id', 'Choose a valid record.', 422);
  return id;
};
const requiredText = (value, label, max = 20000) => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text || text.length > max) throw appealError('appeal_information_required', `${label} is required and must be at most ${max} characters.`, 422);
  return text;
};

function normalizeAppealRequest(input = {}) {
  return {
    reason: requiredText(input.reason, 'Reason for appeal'),
    requestedChange: requiredText(input.requestedChange, 'What they are appealing'),
    evidence: typeof input.evidence === 'string' ? input.evidence.trim().slice(0, 20000) : '',
  };
}

function mapAppeal(row) {
  if (!row) return null;
  return {
    id: Number(row.id), caseId: Number(row.case_id), status: row.status, version: Number(row.version),
    createdByStaffProfileId: Number(row.created_by_staff_profile_id),
    assignedToStaffProfileId: row.assigned_to_staff_profile_id ? Number(row.assigned_to_staff_profile_id) : null,
    originalDecision: parseJson(row.original_decision_json, {}), request: parseJson(row.request_json, {}),
    work: parseJson(row.work_json, []), resolution: parseJson(row.resolution_json),
    createdAt: row.created_at, updatedAt: row.updated_at, resolvedAt: row.resolved_at,
  };
}

function assertAppealVersion(appeal, expectedVersion) {
  if (!Number.isInteger(expectedVersion) || expectedVersion !== appeal.version) {
    throw appealError('appeal_stale', 'This appeal changed. Reload it before continuing.');
  }
}

function decideAppealTransition({ appeal, action, note, actorId, role, work = appeal.work }) {
  if (!['pending', 'in_progress'].includes(appeal.status)) throw appealError('appeal_concluded', 'This appeal has already concluded.');
  const decisionMaker = isAppealDecisionMaker(role);
  if (!decisionMaker && !(action === 'withdraw' && actorId === appeal.createdByStaffProfileId)) {
    throw appealError('appeal_decision_forbidden', 'Only a Decision Maker can decide or authorize reconsideration.', 403);
  }
  if (action === 'change' || action === 'reassess') {
    const mode = action === 'change' ? 'direct' : 'delegated';
    if (work.some(item => !['planned', 'approved', 'denied', 'withdrawn'].includes(item.status) && item.mode !== mode)) {
      throw appealError('appeal_work_already_started', 'The linked work is already in progress. Continue through its current review stage, or stop that reconsideration before choosing a different approach.');
    }
    return { status: 'in_progress', resolution: null, mode: action === 'change' ? 'direct' : 'delegated' };
  }
  if (!['keep', 'withdraw'].includes(action)) throw appealError('appeal_action_invalid', 'Choose an appeal action.', 422);
  const explanation = requiredText(note, 'Explanation');
  if (work.some(item => !['planned', 'approved', 'denied', 'withdrawn'].includes(item.status))) {
    throw appealError('appeal_work_unfinished', 'Complete or withdraw the linked reconsideration work first.');
  }
  const changed = work.some(item => item.status === 'approved' && item.applied === true);
  if (action === 'keep' && changed) throw appealError('appeal_already_changed', 'Funding has changed through this appeal. Finish or stop the remaining reconsideration work.');
  return {
    status: action === 'withdraw' ? 'withdrawn' : 'resolved',
    resolution: { outcome: action === 'withdraw' ? 'withdrawn' : changed ? 'changed' : 'unchanged', note: explanation, actorStaffProfileId: actorId },
  };
}

module.exports = { parseJson, roleKey, isAppealDecisionMaker, appealError, positiveId, requiredText, normalizeAppealRequest, mapAppeal, assertAppealVersion, decideAppealTransition };
